import { Close } from "@mui/icons-material";
import { Box, Grid, Modal, Typography } from "@mui/material";
import * as React from "react";
import { FC, useLayoutEffect, useRef, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { toast } from "react-toastify";

import Button from "../../../common/Button";

import { responseStatus, userStatus } from "../../../../constants/user";
import { RootState, useAppDispatch } from "../../../../redux/store";
import { setLoadingAction } from "../../../../redux/utils/actions";
import patientService from "../../../../services/patient";
import { getNextStatus } from "../../../../utils/helper";

import styles from "../styles.module.scss";
import { billSummaryInitialData } from "../utils";
import AdmissionHistoryLogs from "./AdmissionHistoryLogs";
import PatientAdmissionInformation from "./PatientAdmissionInformation";
import StickyFormSection, { IBillSummary } from "./StickyFormSection";

const PatientDialog: FC<{
  open: boolean;
  setOpen: Function;
  id: string;
  callback?: () => void;
}> = ({ open, setOpen, id, callback }) => {
  // const patient: any = useSelector((state: RootState) => state.patient.patient);
  const [patient, setPatient] = React.useState<any>({});
  const [statusList, setStatusList] = React.useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = React.useState<string>("");
  const [selectedResponseStatus, setSelectedResponseStatus] =
    React.useState<string>("");
  const [remark, setRemark] = React.useState<string>("");
  const [uhid, setUhId] = React.useState<string>("");
  const [uhidAssignedDate, setUhidAssignedDate] = React.useState<string>("");
  const [admittedUnderDr, setAdmittedUnderDr] = React.useState<string>("");
  const [ipNumber, setIpNumber] = React.useState<string>("");
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const modalFooterRef = useRef<HTMLDivElement>(null);
  const [modalBodyWidth, setModalBodyWidth] = useState<number>(0);
  const [modalFooterHeight, setModalFooterHeight] = useState<number>(0);
  const [isDisabledSendBtn, setIsDisabledSendBtn] = useState(true);
  const [hasPendingDoc, setHasPendingDoc] = useState(false);
  const [billSummary, setBillSummary] = useState<IBillSummary>(
    billSummaryInitialData
  );
  const [isStablePatient, setIsStablePatient] = React.useState<boolean>(false);
  const [estimateApprovalDocs, setEstimateApprovalDocs] = useState<
    { label: string; path: string }[]
  >([]);
  const [enhanceApprovalDocs, setEnhanceApprovalDocs] = useState<
    { label: string; path: string }[]
  >([]);

  const dispatch = useAppDispatch();

  const { role } = useSelector(
    (state: RootState) => state?.auth?.profileData?.user,
    shallowEqual
  );

  const FOLLOW_UP_ARRAY = [
    userStatus.FOLLOW_UP_ONE,
    userStatus.FOLLOW_UP_THREE,
    userStatus.FOLLOW_UP_SEVEN,
    userStatus.FOLLOW_UP_FIFTEEN,
  ];

  const selectStatus = (status: string) => {
    if (status !== selectedStatus) {
      setSelectedStatus(status);
      resetStatus();
    }
  };

  const selectResponseStatus = (status: string) => {
    if (status !== selectedResponseStatus) {
      setSelectedResponseStatus(status);
    }
  };

  const doctorNameHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAdmittedUnderDr(e.target.value);
  };

  const ipNumberHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIpNumber(e.target.value);
  };

  const updateStatus = async () => {
    if (patient.status !== selectedStatus) {
      const body = {
        ...(admittedUnderDr !== "" && { admittedUnderDr }),
        ...(selectedStatus !== "" && { status: selectedStatus }),
        ...(FOLLOW_UP_ARRAY.includes(selectedStatus) &&
          selectedResponseStatus !== "" && {
            isResponded:
              selectedResponseStatus === responseStatus.RESPONDED
                ? true
                : false,
          }),
        ...(remark !== "" && { remark }),
        isStablePatient,
        ...(uhid !== "" && { uhid }),
        ...(uhidAssignedDate !== "" && { uhidAssignedDate }),
        ...(ipNumber !== "" && { ipNumber }),
        ...([
          userStatus.DISCHARGED,
          userStatus.LAMA,
          userStatus.EXPIRED,
          userStatus.CLOSED,
        ].includes(selectedStatus) && { billSummary }),
        ...(estimateApprovalDocs.length !== 0 && {
          estimateApprovalDocs: estimateApprovalDocs,
        }),
        ...(enhanceApprovalDocs.length !== 0 && {
          enhanceApprovalDocs: enhanceApprovalDocs,
        }),
      };
      // updatePatient(id, body)(dispatch); // Jubair will change this component states to redux state
      try {
        setIsSubmitting(true);
        dispatch(setLoadingAction(true));

        await patientService.update(id, body);
        const res = await patientService.getById(id);
        const patientData = res.data.data;
        const allAvailableStatus = getNextStatus(
          patientData,
          role,
          isStablePatient
        );
        setPatient(patientData);
        setStatusList(allAvailableStatus);
        setSelectedStatus(patientData?.status);
        if (patientData?.billSummary) {
          setBillSummary(patientData?.billSummary);
        }
        toast.success(res.data.msg);

        setIsSubmitting(false);
        dispatch(setLoadingAction(false));
        setOpen(false);
      } catch (error: any) {
        toast.error(error.response.data.msg);

        setIsSubmitting(false);
        dispatch(setLoadingAction(false));
      }

      setRemark("");
    }
  };

  React.useEffect(() => {
    const getPatientData = async () => {
      try {
        dispatch(setLoadingAction(true));
        const res = await patientService.getById(id);
        dispatch(setLoadingAction(false));
        const patientData = res?.data?.data;
        // setSelectedStatus(patientData?.status);
        const allAvailableStatus = getNextStatus(
          patientData,
          role,
          isStablePatient
        );
        setStatusList(allAvailableStatus);
        setPatient(patientData);
        if (patientData.status === userStatus.SUBMITTED) {
          const canUpdate = await patientService.canUpdate(
            patientData.submittedAt
          );
          setHasPendingDoc(!canUpdate.data?.data);
        }
        if (patientData?.billSummary) {
          setBillSummary(patientData?.billSummary);
        }
        if (!!patientData?.isStablePatient) {
          setIsStablePatient(true);
        }
      } catch (error: any) {
        dispatch(setLoadingAction(false));
      }
    };
    getPatientData();
  }, [dispatch, id, role, isStablePatient]);

  React.useEffect(() => {
    return () => {
      callback?.();
    };
    // this will be called when the popover is closed only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    if (modalBodyRef.current) {
      setModalBodyWidth(modalBodyRef.current.offsetWidth);
    }
    if (modalFooterRef.current) {
      setModalFooterHeight(modalFooterRef.current.offsetHeight);
    }
  });

  React.useEffect(() => {
    let isDisable = true;
    switch (true) {
      case isSubmitting ||
        statusList.length === 0 ||
        patient.status === selectedStatus:
        isDisable = true;
        console.log("Disabled Case 1");
        break;
      case selectedStatus === userStatus.DRAFTED:
        isDisable = true;
        console.log("Disabled Case 2");
        break;
      case selectedStatus === userStatus.ADMITTED &&
        (uhid === "" ||
          admittedUnderDr === "" ||
          uhidAssignedDate === "" ||
          ipNumber === ""):
        isDisable = true;
        console.log("Disabled Case 3");
        break;
      case remark === "":
        isDisable = true;
        break;
      case selectedStatus === userStatus.REJECTED && remark === "":
        console.log("Disabled Case 4");
        isDisable = true;
        break;
      case selectedStatus === userStatus.ESTIMATE_APPROVAL_REQUEST &&
        (estimateApprovalDocs.length === 0 || remark === ""):
        console.log("Disabled Case 5");
        isDisable = true;
        break;
      case selectedStatus.split("_(")[0] ===
        userStatus.ENHANCE_APPROVAL_REQUEST &&
        (enhanceApprovalDocs.length === 0 || remark === ""):
        console.log("Disabled Case 6");
        isDisable = true;
        break;
      case [
        userStatus.DISCHARGED,
        userStatus.LAMA,
        userStatus.EXPIRED,
      ].includes(selectedStatus) &&
        (!billSummary.billAmount ||
          !billSummary.billNumber ||
          !billSummary.billDocuments.length ||
          !remark):
        console.log("Disabled Case 7");
        isDisable = true;
        break;
      case [userStatus.CLOSED].includes(selectedStatus) &&
        (!billSummary.closedBillAmount || !remark):
        isDisable = true;
        console.log("Disabled Case 8");
        break;
      default:
        console.log("Disabled Case 9");
        isDisable = false;
        break;
    }
    setIsDisabledSendBtn(isDisable);
  }, [
    remark,
    admittedUnderDr,
    isSubmitting,
    patient.status,
    selectedStatus,
    statusList.length,
    uhid,
    uhidAssignedDate,
    billSummary,
    estimateApprovalDocs,
    enhanceApprovalDocs,
    ipNumber,
  ]);

  const resetStatus = () => {
    setSelectedResponseStatus("");
    setRemark("");
    setUhId("");
    setUhidAssignedDate("");
    setAdmittedUnderDr("");
    setEstimateApprovalDocs([]);
    setEnhanceApprovalDocs([]);
    setBillSummary(billSummaryInitialData);
    setIpNumber("");
  };

  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      onBackdropClick={() => setOpen(false)}
    >
      <Box className={styles.dialogBox}>
        <div className={styles.modalHeader}>
          <div className={styles.flexAlignCenter}>
            <Typography
              className={styles.modalTitle}
              id="modal-modal-title"
              variant="h6"
              component="h2"
            >
              {patient?.name}
            </Typography>
            <p className={styles.idNumber}>#{patient?.ID?.toUpperCase()}</p>
            {hasPendingDoc && (
              <small className={styles.idNumber}>
                (Kindly give approval to the previous one before proceeding to
                the next.)
              </small>
            )}
          </div>
          <Button className={styles.close} onClick={() => setOpen(false)}>
            <Close />
          </Button>
        </div>
        <div className={styles.modalBody}>
          <div ref={modalBodyRef}></div> {/* this is for getting the width */}
          <Box className="mobile-show">
            <StickyFormSection
              modalFooterRef={modalFooterRef}
              modalBodyWidth={modalBodyWidth}
              selectedStatus={selectedStatus}
              statusList={statusList}
              patient={patient}
              selectStatus={selectStatus}
              selectedResponseStatus={selectedResponseStatus}
              selectResponseStatus={selectResponseStatus}
              uhidAssignedDate={uhidAssignedDate}
              uhid={uhid}
              setUhId={setUhId}
              admittedUnderDr={admittedUnderDr}
              doctorNameHandler={doctorNameHandler}
              ipNumber={ipNumber}
              ipNumberHandler={ipNumberHandler}
              setUhidAssignedDate={setUhidAssignedDate}
              updateStatus={updateStatus}
              isDisabledSendBtn={isDisabledSendBtn}
              hasPendingDoc={hasPendingDoc}
              remark={remark}
              setRemark={setRemark}
              FOLLOW_UP_ARRAY={FOLLOW_UP_ARRAY}
              billSummary={billSummary}
              setBillSummary={setBillSummary}
              stablePatientState={[isStablePatient, setIsStablePatient]}
              estimateApprovalDocs={[
                estimateApprovalDocs,
                setEstimateApprovalDocs,
              ]}
              enhanceApprovalDocs={[
                enhanceApprovalDocs,
                setEnhanceApprovalDocs,
              ]}
            />
          </Box>
          <Grid container spacing={0}>
            <PatientAdmissionInformation
              FOLLOW_UP_ARRAY={FOLLOW_UP_ARRAY}
              patient={patient}
            />
            <AdmissionHistoryLogs
              modalFooterHeight={modalFooterHeight}
              patient={patient}
            />
          </Grid>
        </div>
        <Box className="desktop-show">
          <StickyFormSection
            modalFooterRef={modalFooterRef}
            modalBodyWidth={modalBodyWidth}
            selectedStatus={selectedStatus}
            statusList={statusList}
            patient={patient}
            selectStatus={selectStatus}
            selectedResponseStatus={selectedResponseStatus}
            selectResponseStatus={selectResponseStatus}
            uhidAssignedDate={uhidAssignedDate}
            uhid={uhid}
            setUhId={setUhId}
            admittedUnderDr={admittedUnderDr}
            doctorNameHandler={doctorNameHandler}
            ipNumber={ipNumber}
            ipNumberHandler={ipNumberHandler}
            setUhidAssignedDate={setUhidAssignedDate}
            updateStatus={updateStatus}
            isDisabledSendBtn={isDisabledSendBtn}
            hasPendingDoc={hasPendingDoc}
            remark={remark}
            setRemark={setRemark}
            FOLLOW_UP_ARRAY={FOLLOW_UP_ARRAY}
            billSummary={billSummary}
            setBillSummary={setBillSummary}
            stablePatientState={[isStablePatient, setIsStablePatient]}
            estimateApprovalDocs={[
              estimateApprovalDocs,
              setEstimateApprovalDocs,
            ]}
            enhanceApprovalDocs={[enhanceApprovalDocs, setEnhanceApprovalDocs]}
          />
        </Box>
      </Box>
    </Modal>
  );
};
export default PatientDialog;
